import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService, HttpResponse, HttpStatusEnum } from '@core/api';
import { TranslateService } from '@ngx-translate/core';
import { AppRoutes, ToasterHelper } from '@shared/helpers';
import { UserGroup } from '@shared/interfaces';
import { MessageService } from 'primeng/api';
import {
    EditGroupGeneralSettingsPayload,
    GroupGeneralSettings,
    GroupGeneralSettingsDTO,
    SearchedUser,
    SearchedUserDTO,
} from '../../group-settings/_core/interfaces';
import { EditMemberRoles } from '../../group-settings/_core/interfaces/edit-member-roles-payload.interface';
import { GroupMember } from '../../group-settings/_core/interfaces/member.interface';
import {
    CreateNewGroupDTO,
    CreateNewGroupPayload,
    CreateNewGroupResponseData,
    JoinExistingGroupDTO,
    JoinExistingGroupResponseData,
} from '../../groups-management/_core/interfaces';
import {
    CurrentGroupInfoDTO,
    EditGroupPricingPayload,
    GroupListDTO,
    GroupListPayload,
    GroupListResponseData,
    GroupPricePrepareData,
    GroupPricePrepareDataDTO,
    GroupSettingsHeader,
    GroupSettingsHeaderDTO,
    PrepareGroupsListData,
    PrepareGroupsListDTO,
} from '../interfaces';
import { GroupsMapperService } from './groups.mapper';

@Injectable({ providedIn: 'root' })
export class GroupsApi {
    private readonly resourceUrl = 'groups';

    constructor(
        private apiService: ApiService,
        private messageService: MessageService,
        private translateService: TranslateService,
        private router: Router,
        private groupsMapperService: GroupsMapperService
    ) {}

    getMembersList(groupId: number): Promise<HttpResponse<GroupMember[]>> {
        return new Promise<HttpResponse<GroupMember[]>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/${groupId}/members`).subscribe({
                next: (res: any) => {
                    resolve(this.groupsMapperService.mapGetGroupMembers(res));
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    transferOwnership(memberId: number, groupId: number, memberName: string): Promise<HttpResponse<void>> {
        return new Promise<HttpResponse<void>>(resolve => {
            this.apiService.patch(`${this.resourceUrl}/${groupId}/transfer-ownership?memberId=${memberId}`).subscribe({
                next: () => {
                    resolve({ data: null, status: HttpStatusEnum.SUCCESS });
                    this.messageService.add(
                        ToasterHelper.success(
                            this.translateService.instant('group.settings.transferOwnership.success.message', {
                                memberName: memberName,
                            })
                        )
                    );
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    searchLabUsers(searchBy: string) {
        return new Promise<HttpResponse<SearchedUser[]>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/search-users?search=${searchBy}`).subscribe({
                next: (res: SearchedUserDTO) => {
                    resolve(this.groupsMapperService.mapSearchedUsersFromDTOtoModel(res));
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    inviteUserInGroup(groupId: number, email: string) {
        return new Promise<HttpResponse<string>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/${groupId}/invite?email=${email}`).subscribe({
                next: (res: { message: string }) => {
                    this.messageService.add(
                        ToasterHelper.success(
                            this.translateService.instant('group.members.invite.successMessage', {
                                email: email,
                            })
                        )
                    );
                    resolve({ data: res.message, status: HttpStatusEnum.SUCCESS });
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    deactivateGroup(groupId: number, groupName: string): Promise<HttpResponse<void>> {
        return new Promise<HttpResponse<void>>(resolve => {
            this.apiService.patch(`${this.resourceUrl}/${groupId}/deactivate`).subscribe({
                next: () => {
                    this.messageService.add(
                        ToasterHelper.info(
                            this.translateService.instant('group.settings.deleteGroupModal.success', {
                                groupName: groupName,
                            })
                        )
                    );
                    resolve({ data: null, status: HttpStatusEnum.SUCCESS });
                },
                error: async () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    restoreGroup(groupId: number, groupName: string) {
        return new Promise<HttpResponse<void>>(resolve => {
            this.apiService.patch(`${this.resourceUrl}/${groupId}/activate`).subscribe({
                next: () => {
                    this.messageService.add(
                        ToasterHelper.success(
                            this.translateService.instant('group.settings.restoreGroupModal.success', {
                                groupName: groupName,
                            })
                        )
                    );
                    resolve({ data: null, status: HttpStatusEnum.SUCCESS });
                },
                error: async () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    getGroupSettingsHeader(groupId: number, userId: number) {
        return new Promise<HttpResponse<GroupSettingsHeader>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/${groupId}/header`).subscribe({
                next: (res: GroupSettingsHeaderDTO) => {
                    resolve(this.groupsMapperService.mapGroupSettingsHeaderDTOtoModel(res, userId));
                },
                error: async () => {
                    await this.router.navigateByUrl(AppRoutes.routes().GROUPS_LISTING);
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    getGroupGeneralDetails(groupId: number) {
        return new Promise<HttpResponse<GroupGeneralSettings>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/${groupId}/details`).subscribe({
                next: (res: GroupGeneralSettingsDTO) => {
                    resolve(this.groupsMapperService.mapGroupGeneralSettingsDTOtoModel(res));
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    removeMemberFromGroup(groupId: number, memberId: number, memberName: string) {
        return new Promise<HttpResponse<void>>(resolve => {
            this.apiService.delete(`${this.resourceUrl}/${groupId}/remove-member?memberId=${memberId}`).subscribe({
                next: () => {
                    this.messageService.add(
                        ToasterHelper.success(
                            this.translateService.instant('group.members.removeMember.successMessage', {
                                memberName: memberName,
                            })
                        )
                    );
                    resolve({ data: null, status: HttpStatusEnum.SUCCESS });
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    editGroupGeneralDetails(groupId: number, payload: EditGroupGeneralSettingsPayload) {
        return new Promise<HttpResponse<string>>(resolve => {
            this.apiService.put(`${this.resourceUrl}/${groupId}/edit`, payload).subscribe({
                next: (res: { message: string }) => {
                    resolve({ data: res.message, status: HttpStatusEnum.SUCCESS });
                    this.messageService.add(
                        ToasterHelper.success(
                            this.translateService.instant('group.settings.saveChanges.successMessage')
                        )
                    );
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    editMembersRoles(groupId: number, editedGroupRoles: EditMemberRoles) {
        return new Promise<HttpResponse<void>>(resolve => {
            this.apiService.put(`${this.resourceUrl}/${groupId}/edit-roles`, editedGroupRoles).subscribe({
                next: () => {
                    this.messageService.add(
                        ToasterHelper.success(this.translateService.instant('group.members.editRoles.successMessage'))
                    );
                    resolve({ data: null, status: HttpStatusEnum.SUCCESS });
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    leaveGroup(groupId: number, groupName: string) {
        return new Promise<HttpResponse<void>>(resolve => {
            this.apiService.delete(`${this.resourceUrl}/${groupId}/leave`).subscribe({
                next: () => {
                    this.messageService.add(
                        ToasterHelper.success(
                            this.translateService.instant('group.settings.leaveModal.successMessage', {
                                groupName: groupName,
                            })
                        )
                    );
                    resolve({ data: null, status: HttpStatusEnum.SUCCESS });
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    createNewGroup(payload: CreateNewGroupPayload): Promise<HttpResponse<CreateNewGroupResponseData>> {
        return new Promise<HttpResponse<CreateNewGroupResponseData>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/create`, payload).subscribe({
                next: (res: CreateNewGroupDTO) => {
                    resolve(this.groupsMapperService.mapCreateNewGroupResponse(res));
                    this.messageService.add(
                        ToasterHelper.success(
                            this.translateService.instant('groups.management.createGroup.successMessage', {
                                groupName: payload.name,
                            })
                        )
                    );
                },
                error: async () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    joinExistingGroup(enrollmentKey: string): Promise<HttpResponse<JoinExistingGroupResponseData>> {
        return new Promise<HttpResponse<JoinExistingGroupResponseData>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/enroll?key=${enrollmentKey}`).subscribe({
                next: (res: JoinExistingGroupDTO) => {
                    resolve(this.groupsMapperService.mapJoinExistingGroupResponse(res));
                    this.messageService.add(
                        ToasterHelper.success(
                            this.translateService.instant('groups.management.joinGroup.successMessage', {
                                groupName: res.name,
                            })
                        )
                    );
                },
                error: async () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    getGroupsList(payload: GroupListPayload): Promise<HttpResponse<GroupListResponseData>> {
        return new Promise<HttpResponse<GroupListResponseData>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/get-list`, payload).subscribe({
                next: (res: GroupListDTO) => {
                    resolve(this.groupsMapperService.mapGetListData(res));
                },
                error: async () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    getPrepareGroupsList(): Promise<HttpResponse<PrepareGroupsListData>> {
        return new Promise<HttpResponse<PrepareGroupsListData>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/prepare-get-list`).subscribe({
                next: (res: PrepareGroupsListDTO) => {
                    resolve(this.groupsMapperService.mapGetPrepareData(res));
                },
                error: async () => {
                    await this.router.navigateByUrl(AppRoutes.routes().ERROR);
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    getCurrentGroupInfo(groupId: number): Promise<HttpResponse<UserGroup>> {
        return new Promise<HttpResponse<UserGroup>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/current-group-info/${groupId}`).subscribe({
                next: (res: CurrentGroupInfoDTO) => {
                    resolve(this.groupsMapperService.mapGetCurrentGroupInfo(res));
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    getEditPricePrepareData(groupId: number): Promise<HttpResponse<GroupPricePrepareData>> {
        return new Promise<HttpResponse<GroupPricePrepareData>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/${groupId}/prepare-edit-price`).subscribe({
                next: (res: GroupPricePrepareDataDTO) => {
                    resolve(this.groupsMapperService.mapGetGroupPricePrepareData(res));
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    editGroupPrice(groupId: number, payload: EditGroupPricingPayload): Promise<HttpResponse<{ message: string }>> {
        return new Promise<HttpResponse<{ message: string }>>(resolve => {
            this.apiService.put(`${this.resourceUrl}/${groupId}/edit-price`, payload).subscribe({
                next: (res: { message: string }) => {
                    resolve({ data: { message: res.message }, status: HttpStatusEnum.SUCCESS });
                    this.messageService.add(
                        ToasterHelper.success(
                            this.translateService.instant('group.settings.editPricing.success.message')
                        )
                    );
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }
}
